"use client";

import {useEffect, useRef} from "react";

const supportedTrackingSearchParameters = {
    utm_medium: 'utm_medium',
    utm_source: 'utm_source',
    utm_campaign: 'utm_campaign',
    utm_term: 'utm_term',
    gclid: 'gclid',
    gbraid: 'gbraid',
    wbraid: 'wbraid',
    // Microsoft Ads appends its click ID to landing URLs as `msclkid`
    msclkid: 'microsoft_ad_click_id',
    microsoft_ad_click_id: 'microsoft_ad_click_id',
    // Ads in ChatGPT append their click ID to landing URLs as `oppref`
    // https://help.openai.com/en/articles/20001409-conversion-measurement
    oppref: 'chatgpt_ad_click_id',
    chatgpt_ad_click_id: 'chatgpt_ad_click_id',
} as const;

type StorageValue = {
    utm_medium?: string;
    utm_source?: string;
    utm_campaign?: string;
    utm_term?: string;
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    microsoft_ad_click_id?: string;
    chatgpt_ad_click_id?: string;
    referrer_domain?: string,
    initial_path?: string,
}

const maximumAllowedValueCharacterLength = 128;

const storageKey = 'd0_utm';

// Referrers from these domains (or any of their subdomains) are internal navigation, so they
// must not overwrite `referrer_domain`. Clerk (clerk.dash0.com) and sign-in (accounts.dash0.com)
// are covered by `dash0.com`.
const excludedReferrerDomains = ['dash0.com'] as const;

function isExcludedReferrer(referrer: string): boolean {
    let hostname: string;
    try {
        hostname = new URL(referrer).hostname.toLowerCase();
    } catch {
        return false;
    }
    return excludedReferrerDomains.some(domain => hostname === domain || hostname.endsWith(`.${domain}`));
}

/**
 * Merges the current page's tracking data into the stored value. Pure, so it can be unit tested.
 *
 * `initial_path` only moves when the visit brings new attribution (a changed UTM / click ID or an
 * external referrer). Internal navigation still triggers a save, which refreshes the cookie expiry.
 */
export function updateTrackingData(
    stored: StorageValue,
    search: string,
    referrer: string,
    pathname: string,
): { value: StorageValue; shouldSave: boolean } {
    const value = {...stored};
    let hasAttributionChanges = false;
    let shouldSave = false;

    const searchParams = new URLSearchParams(search);
    for (const [searchParameterKey, storageField] of Object.entries(supportedTrackingSearchParameters) as [string, keyof StorageValue][]) {
        let parameterValue = searchParams.get(searchParameterKey);
        if (!parameterValue) {
            continue;
        }

        parameterValue = parameterValue.substring(0, maximumAllowedValueCharacterLength);

        if (parameterValue !== value[storageField]) {
            value[storageField] = parameterValue;
            hasAttributionChanges = true;
        }
    }

    if (referrer) {
        shouldSave = true;
        if (!isExcludedReferrer(referrer)) {
            value.referrer_domain = referrer;
            hasAttributionChanges = true;
        }
    }

    if (hasAttributionChanges) {
        value.initial_path = pathname;
    }

    return {value, shouldSave: hasAttributionChanges || shouldSave};
}

/**
 * This component...
 *
 *  - identifies when a user lands on dash0.com with UTM parameters
 *  - remembers this search parameter across reloads by storing them in a cookie
 *
 * This cookie is interpreted within our product UI.
 */
export function CampaignPersister() {
    const ref = useRef<StorageValue>({});

    // This effect synchronizes the tracking parameters from the URL and from cookie storage to ref.current
    useEffect(() => {
        doTracking().catch(e => console.debug('Tracking failed', e));

        async function doTracking() {
            ref.current = await loadTrackingDataFromStorage();

            const {value, shouldSave} = updateTrackingData(ref.current, window.location.search, document.referrer, window.location.pathname);
            ref.current = value;

            if (shouldSave) {
                await saveTrackingDataToStorage(ref.current);
            }
        }
    }, []);

    return null;
}



async function loadTrackingDataFromStorage(): Promise<StorageValue> {
    try {
        const storedValue = await window.cookieStore.get(storageKey);
        if (!storedValue?.value) {
            return {};
        }

        return JSON.parse(storedValue.value);
    } catch (e) {
        console.info('Failed to load tracking data.', e)
        return {};
    }
}

function cookieDomain(): string | undefined {
    const hostname = window.location.hostname;
    return hostname.includes('dash0.com') ? 'dash0.com' : undefined;
}

async function saveTrackingDataToStorage(value: StorageValue) {
    const domain = cookieDomain();
    try {
        if (typeof window.cookieStore !== 'undefined') {
            await window.cookieStore.set({
                name: storageKey,
                value: JSON.stringify(value),
                ...(domain ? { domain } : {}),
                path: '/',
                sameSite: 'lax',
                expires: Date.now() + 1000 * 60 * 60 * 24,
            });
            const verification = await window.cookieStore.get(storageKey);
            if (verification?.value) {
                return;
            }
        }
    } catch (e) {
        console.info('Failed to store UTM data.', e);
    }
    try {
        const encoded = encodeURIComponent(JSON.stringify(value));
        const expires = new Date(Date.now() + 1000 * 60 * 60 * 24).toUTCString();
        const domainPart = domain ? `;domain=${domain}` : '';
        document.cookie = `${storageKey}=${encoded};path=/${domainPart};expires=${expires};SameSite=Lax`;
    } catch (e) {
        console.info('Failed to store UTM data.', e);
    }
}